import React, { useEffect, useRef, useState } from 'react';
import { updateLayoutColumns } from 'flow/actions/app-actions';
import { showCallDialogForExtensionKey } from 'flow/actions/call-dialog-actions';
import { useModal } from 'flow/contexts/ModalProvider';
import useAppContext from 'flow/hooks/useAppContext';
import useSinglePaneMode from 'flow/hooks/useSinglePaneMode';
import {
    getNumLocalCalls,
    getOwnActiveCalls,
    hasActiveCallsBlockingNewCall,
} from 'flow/selectors/call-selectors';
import {
    selectMeetingIsInFocus,
    selectViewerIsInMeeting,
} from 'flow/selectors/meeting-selectors';
import { Contact } from 'flow/types/Contact';
import { addUserPhoneLogEntry } from 'flow/utils/browser-utils';
import { NewCallBlockReason } from 'flow/utils/call-utils';
import { getFirstActiveCallWithDirection } from 'flow/utils/prioritized-active-call-utils';
import { DisplayMode } from 'flow/utils/rtc-utils';
import { twMerge } from 'tailwind-merge';
import BottomBarFooter from '../communication-canvas/BottomBarFooter';
import CallFooter from '../communication-canvas/CallFooter';
import CallHeader from '../communication-canvas/CallHeader';
import CallList from '../communication-canvas/CallList';
import CSSTransitionTW from '../communication-canvas/CSSTransition';
import MeetingFooter from '../meeting/MeetingFooter';
import MeetingMinimizedFooter from '../meeting/MeetingMinimizedFooter';
import MeetingHeader from '../meeting/MeetingHeader';
import { clearSelectedThreadMessage } from 'flow/actions/chat-actions';
import { useAppDispatch, useAppSelector } from 'flow/hooks/useRedux';
import { Statuses } from 'flow/types/Extension';

interface CommunicationCanvasProps {
    setShowCanvas: (show: boolean) => void;
}

const CommunicationCanvas = ({ setShowCanvas }: CommunicationCanvasProps) => {
    const { i18next, showDialpad, setShowDialpad } = useAppContext();
    const dispatch = useAppDispatch();
    const { openModal } = useModal();

    const { isSinglePaneMode } = useSinglePaneMode();

    const [transitionExitTimeout, setTransitionExitTimeout] = useState(2000);
    const [prevDialpadState, setPrevDialpadState] = useState(false);

    const renderLocalCalls = useAppSelector(
        (state) => getNumLocalCalls(state) !== 0,
    );

    const activeCalls = useAppSelector(getOwnActiveCalls);

    const remoteCall = getFirstActiveCallWithDirection(activeCalls);

    const hasActiveMeeting = useAppSelector(selectViewerIsInMeeting);

    const meetingInFullscreen = useAppSelector(
        (state) =>
            state.meeting.getIn(['activeMeeting', 'meetingView']) ===
            DisplayMode.FULLSCREEN.key,
    );

    const activeMeetingCallId = useAppSelector((state) =>
        state.meeting.getIn(['activeMeeting', 'call', 'callId']),
    );

    const preferredCallCanvasSize = useAppSelector((state) =>
        state.app.get('callCanvasSize'),
    );

    const meetingIsInFocus = useAppSelector(selectMeetingIsInFocus);

    const showCanvas = renderLocalCalls || !!remoteCall || hasActiveMeeting;

    const remoteCallIsConferenceCall =
        remoteCall?.has('callId') &&
        activeMeetingCallId === remoteCall?.get('callId');

    const renderRemoteCall =
        !renderLocalCalls && !!remoteCall && !remoteCallIsConferenceCall;

    const canStartNewCall = useAppSelector(
        (state) =>
            hasActiveCallsBlockingNewCall(state, 'outgoing', 2) ===
            NewCallBlockReason.NONE,
    );

    useEffect(() => {
        addUserPhoneLogEntry('Comm canvas did mount');
    }, [dispatch]);

    const prevHasActiveMeeting = useRef<boolean>(hasActiveMeeting);
    const prevTransitionExitTimeout = useRef<number>(transitionExitTimeout);

    useEffect(() => {
        if (hasActiveMeeting && meetingIsInFocus) {
            setTransitionExitTimeout(0);
        }
        if (
            prevHasActiveMeeting.current &&
            !hasActiveMeeting &&
            prevTransitionExitTimeout.current === 0
        ) {
            const timeoutId = setTimeout(() => setTransitionExitTimeout(500));
            return () => clearTimeout(timeoutId);
        }

        prevHasActiveMeeting.current = hasActiveMeeting;
        prevTransitionExitTimeout.current = transitionExitTimeout;
    }, [hasActiveMeeting, meetingIsInFocus, transitionExitTimeout]);

    const newCallToContactAction = (
        selectedExtension: {
            key: string;
            extensionContact: Contact;
            presenceData: Statuses;
        },
        number: string,
    ) => {
        setTransitionExitTimeout(0);

        dispatch(showCallDialogForExtensionKey(number, selectedExtension?.key));
    };

    const newCallToNumberAction = (number: string) =>
        dispatch(showCallDialogForExtensionKey(number, null));

    const newCallToPbxAction = (_: any, number: string) =>
        dispatch(showCallDialogForExtensionKey(number, null));

    const startNewCall = () => {
        setTransitionExitTimeout(0);

        openModal('numberPicker', {
            title: i18next.t('call.new'),
            contactSelectedAction: newCallToContactAction,
            numberSelectedAction: newCallToNumberAction,
            pbxSelectedAction: newCallToPbxAction,
        });
    };

    const onExit = () => {
        setShowDialpad(prevDialpadState);
        setShowCanvas(false);
        dispatch(updateLayoutColumns());
    };

    const onEnter = () => {
        setShowCanvas(true);
        setPrevDialpadState(showDialpad);
        dispatch(clearSelectedThreadMessage());
        dispatch(updateLayoutColumns());
    };

    return (
        <CSSTransitionTW
            transitionIn={showCanvas}
            timeout={{
                enter: 300,
                exit: transitionExitTimeout,
            }}
            unmountOnExit={true}
            onEnter={onEnter}
            onExited={onExit}
            id="communication-canvas"
            className="no-dark"
        >
            <div className="no-dark flex">
                {DisplayMode.MINIMIZED.key === preferredCallCanvasSize ? (
                    <div className="flex w-full shrink-0 flex-wrap items-center gap-2 bg-black p-1">
                        {hasActiveMeeting && meetingInFullscreen ? (
                            <MeetingHeader />
                        ) : null}
                        <div
                            className={twMerge(
                                'flex flex-auto gap-2 self-stretch',
                                isSinglePaneMode ? 'flex-col' : null,
                            )}
                        >
                            <CallList
                                remoteCall={
                                    renderRemoteCall ? remoteCall : undefined
                                }
                                hasActiveMeeting={hasActiveMeeting}
                            />
                        </div>
                        {hasActiveMeeting && meetingInFullscreen ? (
                            <MeetingFooter />
                        ) : null}

                        {hasActiveMeeting ? (
                            <MeetingMinimizedFooter />
                        ) : (
                            <BottomBarFooter
                                onNewCall={
                                    canStartNewCall ? startNewCall : null
                                }
                            />
                        )}
                    </div>
                ) : (
                    <div
                        className={twMerge(
                            'z-40 flex shrink-0 flex-col items-center gap-1 bg-black',
                            preferredCallCanvasSize ===
                                DisplayMode.FULLSCREEN.key
                                ? 'w-screen'
                                : 'panel:w-[360px] w-screen min-w-[360px]',
                        )}
                    >
                        {meetingIsInFocus ||
                        (hasActiveMeeting && meetingInFullscreen) ? (
                            <MeetingHeader />
                        ) : (
                            <CallHeader />
                        )}

                        <div className="hide-scrollbar flex w-full flex-col gap-2 overflow-scroll px-2">
                            <CallList
                                remoteCall={
                                    renderRemoteCall ? remoteCall : undefined
                                }
                                hasActiveMeeting={hasActiveMeeting}
                            />
                        </div>

                        <div className="mt-auto w-full">
                            {meetingIsInFocus ||
                            (hasActiveMeeting && meetingInFullscreen) ? (
                                <MeetingFooter />
                            ) : (
                                <CallFooter
                                    onNewCall={
                                        canStartNewCall ? startNewCall : null
                                    }
                                />
                            )}
                        </div>
                    </div>
                )}
            </div>
        </CSSTransitionTW>
    );
};

export default CommunicationCanvas;
